import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { usePublicSettings } from '../../hooks/usePublicSettings'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { login, user, sessionNotice } = useAuth()
  const navigate = useNavigate()
  const settings = usePublicSettings()
  const whatsapp = settings?.['growth.whatsapp_number']
  const brand = settings?.['onboarding.brand_name']

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true })
    }
  }, [user, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await login(email.trim(), password)
      if (result.success) {
        navigate('/', { replace: true })
      } else {
        setError(result.error)   // the server's words: wrong details, or locked for N minutes
      }
    } catch {
      setError('Sign-in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 safe-area-inset">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
            <p className="text-slate-600 mt-2">Sign in to your account</p>
          </div>

          {sessionNotice && !error && (
            <div role="status" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
              {sessionNotice}
            </div>
          )}
          {error && (
            <div role="alert" className="mb-4 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Sign in">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              placeholder="Enter your password"
              required
            />
            <div className="text-right mt-1">
              <Link to="/forgot-password" className="text-sm text-slate-700 hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-3 px-4 rounded-xl font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation shadow-lg hover:shadow-xl transition-shadow"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2"><LoadingSpinner size="sm" /> Signing in…</span>
              ) : 'Sign In'}
            </button>
          </form>

          {/* Customers who use WhatsApp (often without a web password) sign in with a one-time code */}
          <div className="mt-4">
            <div className="flex items-center gap-3 my-4 text-xs text-slate-400" aria-hidden="true">
              <span className="flex-1 border-t border-slate-200" />or<span className="flex-1 border-t border-slate-200" />
            </div>
            <Link
              to="/login/whatsapp"
              className="block w-full text-center border border-slate-300 text-slate-900 py-3 px-4 rounded-xl font-medium hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 touch-manipulation"
            >
              Get a code on WhatsApp
            </Link>
          </div>

          {/* Register Link */}
          <div className="text-center mt-6">
            <p className="text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-slate-900 font-medium hover:underline">
                Sign up
              </Link>
            </p>
          </div>

          {/* Or just carry on in the WhatsApp chat */}
          {whatsapp && (
            <p className="text-center text-xs text-slate-500 mt-4">
              Booked with {brand || 'us'} on WhatsApp?{' '}
              <a href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="underline text-slate-700">
                Continue on WhatsApp
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
